'use client';

import { useEffect } from 'react';
import { SelectItemType } from 'types/common';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface TEXTLOCALSTORE {
  texts: SelectItemType[];
  setTexts: (text: SelectItemType[]) => void;
}

/**
 * "지난 입력" 목록. 브라우저에만 남는다(localStorage).
 *
 * `skipHydration` 이 핵심이다. 이게 없으면 zustand persist 는 스토어를 만드는
 * 순간 localStorage 를 **동기로** 읽어 값을 채운다. 그러면 서버는 빈 목록으로
 * HTML 을 그렸는데 클라이언트의 첫 렌더에는 지난 입력이 들어 있어 트리가
 * 어긋나고, React 가 hydration 을 포기한다(#418). 다시 찾아온 사람만 겪으므로
 * 재현이 잘 안 되고, 오류는 운영 로그에만 쌓인다.
 *
 * 그래서 첫 렌더는 서버와 똑같이 빈 목록으로 두고, 마운트 뒤에 값을 채운다 —
 * `useLocalTexts()` 를 쓰면 그 순서가 지켜진다.
 */
export const useTextLocal = create<TEXTLOCALSTORE>()(
  persist(
    (set) => ({
      texts: [],
      setTexts: (texts) => set({ texts })
    }),
    {
      name: 'text-local-storage', // localStorage key
      skipHydration: true,
      partialize: (state) => ({
        texts: state.texts
      })
    }
  )
);

/**
 * 지난 입력을 읽어 쓰는 컴포넌트용 훅.
 *
 * 마운트 이후에 저장소를 읽어 스토어를 채운다. 렌더 중에는 아무것도 읽지 않으므로
 * 서버 렌더 결과와 첫 클라이언트 렌더가 항상 같다. 이미 채워진 뒤에 다시 호출해도
 * 같은 값을 다시 읽을 뿐이라 문제가 없다.
 */
export function useLocalTexts(): TEXTLOCALSTORE {
  useEffect(() => {
    // 배포 직후 남아 있는 이전 HTML이 새 청크 해시를 참조하거나, 모바일
    // 네트워크에서 청크가 일부만 받아졌을 때 zustand persist 미들웨어가
    // 붙지 않아 `.persist` 자체가 없는 경우가 있다(#288 오류 알림).
    // 그런 순간에도 "지난 입력" 하나 못 읽는 것뿐이므로 페이지 전체를
    // 죽이지 않고 조용히 넘어간다.
    void useTextLocal.persist?.rehydrate();
  }, []);

  return useTextLocal();
}
